// Remix takeover host — app-shell-level provider that runs the SAME staged
// Music → Artwork → Video → Render pipeline (ManualMode) IN PLACE over the
// main content region, seeded from a source library track.
//
// No route change, no react-router navigate, no small modal: when a remix
// is open the staged flow renders as a full-area overlay (RemixTakeoverSlot)
// that visually sits over the page content while the shell chrome
// (sidebar/topbar) stays put. The same URL is preserved — closing returns
// the user exactly where they were.
import {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { LibraryItem } from "../../types";
import type { RenderDetail } from "../renders/RenderSheet";
import type { ManualSeed } from "./GeneratePage";
import { RouteFallback } from "../../app/RouteFallback";

// ManualMode lives in the GeneratePage chunk. Lazy-load it so the remix
// takeover code (and the whole staged pipeline) only ships when a user
// actually opens a remix — it stays out of the entry/shell bundle.
const ManualMode = lazy(() =>
  import("./GeneratePage").then((m) => ({ default: m.ManualMode })),
);

interface RemixFlowApi {
  /** Open the staged remix takeover seeded from a library item. */
  openRemix: (seed: ManualSeed) => void;
  /** Close the takeover and return to the underlying page (same URL). */
  closeRemix: () => void;
  /** The active seed while the takeover is open; null otherwise. */
  seed: ManualSeed | null;
}

const RemixFlowContext = createContext<RemixFlowApi | null>(null);

/**
 * Build a {@link ManualSeed} from a library item. The source track becomes
 * the already-approved music stage.
 *
 * - music / project: the item IS the music render — seed musicJobId from
 *   its own id.
 * - image / video / visualizer: the music render is the item's PARENT.
 *   ``LibraryItem`` carries ``remixOfId`` (the source job this render was
 *   derived from) as the most reliable parent pointer; we prefer it when
 *   present, otherwise fall back to the item's own id so the artwork +
 *   video stages can still proceed (and the inline auto-prompt offer has
 *   a render to read a brief from). Fields used, in priority order:
 *     1. ``item.remixOfId``  — lineage back-pointer to the source render
 *     2. ``item.id``         — last-resort fallback
 */
export function seedFromItem(item: LibraryItem): ManualSeed {
  const isMusicLike = item.kind === "music" || item.kind === "project";
  const musicJobId = isMusicLike ? item.id : item.remixOfId || item.id;
  const title = item.title || "Untitled";
  return {
    sourceRenderId: item.id,
    sourceTitle: title,
    musicJobId,
    musicApproved: true,
    musicRendered: true,
    projectName: `Remix of ${title}`,
    remixOfRenderId: item.id,
  };
}

/**
 * Build a {@link ManualSeed} from a render-detail record — the
 * RenderDetailSheet's remix buttons use this so remix from the sheet
 * runs the SAME in-place staged pipeline the library cards use.
 *
 * Mirrors {@link seedFromItem}. ``RenderDetail`` carries no lineage
 * back-pointer (no ``remixOfId``), so for every kind the source render
 * IS the music we seed from — ``musicJobId`` falls back to its own id.
 * Callers only invoke this when a render is present, but we guard the
 * empty-id case defensively.
 */
export function seedFromRender(render: Partial<RenderDetail>): ManualSeed {
  const id = render.id ?? "";
  const title = render.title || "Untitled";
  return {
    sourceRenderId: id,
    sourceTitle: title,
    musicJobId: id,
    musicApproved: true,
    musicRendered: true,
    projectName: `Remix of ${title}`,
    remixOfRenderId: id,
  };
}

export function RemixFlowProvider({ children }: { children: ReactNode }) {
  const [seed, setSeed] = useState<ManualSeed | null>(null);

  const openRemix = useCallback((s: ManualSeed) => setSeed(s), []);
  const closeRemix = useCallback(() => setSeed(null), []);

  const api = useMemo<RemixFlowApi>(
    () => ({ openRemix, closeRemix, seed }),
    [openRemix, closeRemix, seed],
  );

  return <RemixFlowContext.Provider value={api}>{children}</RemixFlowContext.Provider>;
}

/**
 * Renders the remix takeover overlay. Mount this INSIDE the shell's
 * ``<main>`` (which must be ``position: relative``) so the overlay covers
 * exactly the main content region — sidebar + topbar chrome stay put.
 * Renders nothing when no remix is open.
 */
export function RemixTakeoverSlot() {
  const { seed, closeRemix } = useRemixFlow();
  if (!seed) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Remix of ${seed.sourceTitle}`}
      style={{
        // Covers the <main> content region. Solid background so the
        // underlying page is fully replaced; an internal scroll keeps long
        // pipelines usable. The shell topbar (sticky, above main) and the
        // sidebar (beside main) are outside this box and stay interactive.
        position: "absolute",
        inset: 0,
        zIndex: 40,
        background: "var(--bg)",
        overflowY: "auto",
        // Mirror the GeneratePage outer padding so the staged flow reads
        // identically to the new-track page.
        padding: "24px clamp(16px,4vw,32px) 120px",
      }}
    >
      <div style={{ maxWidth: 1440, margin: "0 auto" }}>
        <Suspense fallback={<RouteFallback />}>
          <ManualMode seed={seed} onExit={closeRemix} />
        </Suspense>
      </div>
    </div>
  );
}

export function useRemixFlow(): RemixFlowApi {
  const ctx = useContext(RemixFlowContext);
  if (!ctx) {
    throw new Error("useRemixFlow must be used within a RemixFlowProvider");
  }
  return ctx;
}
